import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';

/**
 * V2-only fork of {@code RHFSearchableSelectField} (in src/app/common/hook-form).
 * Adds {@code placeholder} + {@code size} prop pass-through to the inner
 * {@link TextField} so the V2 filter bar can render compact 33px-tall fields
 * with sample-style placeholder text — without touching the shared
 * RHFSearchableSelectField that 30+ other pages depend on.
 *
 * Behaviour identical to RHFSearchableSelectField otherwise.
 */
AbdSearchableSelect.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    size: PropTypes.string,
    options: PropTypes.array,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    noOptionsText: PropTypes.string,
};

export default function AbdSearchableSelect({
    name,
    label,
    placeholder,
    size,
    options = [],
    customWidth = 300,
    fullWidth = false,
    noOptionsText = 'No options',
    ...other
}) {
    const { control } = useFormContext();
    const [inputValue, setInputValue] = React.useState('');

    const filteredOptions = React.useMemo(() => {
        const terms = String(inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.length) return options;
        return options.filter((opt) => {
            const lbl = String(opt?.label ?? opt?.value ?? '').toLowerCase();
            return terms.every((t) => lbl.includes(t));
        });
    }, [options, inputValue]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const selectedOption = options.find((opt) => String(opt.value) === String(field.value)) || null;
                return (
                    <Autocomplete
                        {...other}
                        size={size}
                        options={filteredOptions}
                        value={selectedOption}
                        inputValue={inputValue}
                        onInputChange={(_, newInput) => setInputValue(newInput ?? '')}
                        onChange={(_, newVal) => field.onChange(newVal ? newVal.value : '')}
                        filterOptions={(opts) => opts}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : String(opt?.label ?? ''))}
                        isOptionEqualToValue={(opt, val) => String(opt?.value ?? opt) === String(val?.value ?? val)}
                        noOptionsText={noOptionsText}
                        componentsProps={{
                            paper: {
                                sx: {
                                    width: fullWidth ? '100%' : customWidth,
                                    overflowY: 'auto',
                                },
                            },
                        }}
                        ListboxProps={{
                            sx: { maxHeight: 450, overflowY: 'auto', padding: 0 },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                placeholder={placeholder}
                                size={size}
                                error={!!error}
                                helperText={error?.message}
                                required={other.required}
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'off',
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                style={{ color: 'black' }}
                                key={`${String(option.value)}-${String(option.label).slice(0, 30)}`}
                            >
                                {option.label}
                            </li>
                        )}
                    />
                );
            }}
        />
    );
}
